import { useEffect, useState } from "react";
import { fetchDolls, updateDoll, uploadDollImage, resolveImage } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import CuratorPhotos from "@/pages/CuratorPhotos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Save, Loader2 } from "lucide-react";

const DollEditor = ({ doll, onChange }) => {
  const [name, setName] = useState(doll.name);
  const [story, setStory] = useState(doll.story);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateDoll(doll.id, { name, story });
      onChange(updated);
      toast.success(`${updated.name}'s tale has been sealed.`);
    } catch (e) {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadDollImage(doll.id, file);
      onChange({ ...doll, image_url: res.image_url, name, story });
      toast.success("New portrait hung in the cabinet.");
    } catch (err) {
      toast.error("Upload failed. Check the file and try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div
      data-testid={`editor-${doll.id}`}
      className="grid grid-cols-12 gap-6 md:gap-10 py-10 border-b border-[#e8aa42]/15"
    >
      <div className="col-span-12 md:col-span-4">
        <div className="relative doll-arch overflow-hidden border border-[#e8aa42]/20 bg-[#1a1614] aspect-[2/3]">
          <img
            src={resolveImage(doll.image_url)}
            alt={doll.name}
            className="w-full h-full object-cover"
          />
        </div>
        <label
          data-testid={`upload-label-${doll.id}`}
          className="mt-4 flex items-center justify-center gap-2 cursor-pointer border border-[#e8aa42]/40 hover:border-[#e8aa42] hover:bg-[#e8aa42]/10 text-[#e8aa42] font-ui text-xs tracking-eerie uppercase py-3 transition-colors duration-300"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
          ) : (
            <Upload className="w-4 h-4" strokeWidth={1.5} />
          )}
          {uploading ? "Hanging…" : "Replace Portrait"}
          <input
            data-testid={`upload-input-${doll.id}`}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      </div>

      <div className="col-span-12 md:col-span-8 space-y-5">
        <div>
          <p className="font-ui text-[10px] tracking-eerie uppercase text-[#e8aa42]/80 mb-2">
            N° {String(doll.order).padStart(2, "0")} · Lost Girl
          </p>
          <Label
            htmlFor={`name-${doll.id}`}
            className="font-ui text-xs tracking-eerie uppercase text-[#a89f91]"
          >
            Name
          </Label>
          <Input
            id={`name-${doll.id}`}
            data-testid={`name-input-${doll.id}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-2 bg-[#1a1614] border-[#e8aa42]/20 text-[#e2d6c8] font-display text-2xl h-14 focus-visible:ring-[#e8aa42]/40 focus-visible:border-[#e8aa42]/60"
          />
        </div>

        <div>
          <Label
            htmlFor={`story-${doll.id}`}
            className="font-ui text-xs tracking-eerie uppercase text-[#a89f91]"
          >
            Backstory
          </Label>
          <Textarea
            id={`story-${doll.id}`}
            data-testid={`story-input-${doll.id}`}
            value={story}
            onChange={(e) => setStory(e.target.value)}
            rows={10}
            className="mt-2 bg-[#1a1614] border-[#e8aa42]/20 text-[#e2d6c8] font-body text-base leading-relaxed focus-visible:ring-[#e8aa42]/40 focus-visible:border-[#e8aa42]/60"
            placeholder="Write the haunted tale of this doll…"
          />
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid={`save-btn-${doll.id}`}
          className="bg-[#8b0000] hover:bg-[#5c0000] text-[#e2d6c8] font-ui text-xs tracking-eerie uppercase rounded-none h-11 px-8 border border-[#8b0000] hover:border-[#e8aa42]/60 transition-colors duration-500"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />}
          {saving ? "Sealing…" : "Seal the Tale"}
        </Button>
      </div>
    </div>
  );
};

export default function Curator() {
  const [dolls, setDolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("girls");

  useEffect(() => {
    fetchDolls()
      .then((data) => setDolls(data))
      .catch(() => toast.error("Could not summon the cabinet."))
      .finally(() => setLoading(false));
  }, []);

  const updateOne = (updated) => {
    setDolls((prev) => prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)));
  };

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-6">
        <p className="font-ui text-xs tracking-eerie uppercase text-[#e8aa42]/80 mb-4">
          Curator's Archive
        </p>
        <h1 className="font-display font-light text-5xl md:text-6xl text-[#e2d6c8] mb-4">
          {tab === "girls" ? "Tend to the Girls" : "Tend to the Gallery"}
        </h1>
        <p className="font-body text-lg text-[#a89f91] max-w-2xl">
          {tab === "girls"
            ? "Upload a portrait, write or revise each girl's story. Every Lost Girl is kept here for those who come to listen."
            : "Upload photographs, add optional titles and captions, and organise them by category."}
        </p>
      </section>

      {/* Tabs */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-6 pb-2">
        <div className="flex items-center gap-2 border-b border-[#e8aa42]/15">
          {[
            { key: "girls", label: "The Girls" },
            { key: "photos", label: "Photography" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`curator-tab-${t.key}`}
              className={`font-ui text-xs tracking-eerie uppercase px-5 py-3 -mb-px border-b-2 transition-colors duration-500 ${
                tab === t.key
                  ? "border-[#e8aa42] text-[#e8aa42]"
                  : "border-transparent text-[#a89f91] hover:text-[#e2d6c8]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-6 pb-32">
        {tab === "girls" ? (
          loading ? (
            <div
              data-testid="curator-loading"
              className="font-ui text-xs tracking-eerie uppercase text-[#a89f91] py-12"
            >
              Unlocking the cabinet…
            </div>
          ) : (
            dolls.map((doll) => (
              <DollEditor key={doll.id} doll={doll} onChange={updateOne} />
            ))
          )
        ) : (
          <CuratorPhotos />
        )}
      </section>
    </div>
  );
}
